/* Jam: pick a song by difficulty (/jam), then play it as a game (/jam/<song id>). */
import { useEffect } from 'react';
import { useGo } from '../../app/navigation';
import { useSubPath } from '../../app/useSubPath';
import { type PreparedSong, SONGS, songById } from '../../content/songs';
import { buildChart } from '../../core/jam';
import { useProgress } from '../../core/store';
import { fmtTime } from '../../core/util';
import { Icon } from '../../ui/icons';
import { modStyle } from '../../ui/style';
import { Stars } from '../../ui/widgets';
import { GroupTitle } from '../module/ModulePage';
import { LEVEL_NAME } from '../songs/SongList';
import { JamGame } from './JamGame';
import { loadJamRecord } from './records';

export const JAM_ROUTE = 'jam';

const LEVEL_SUB: Record<number, string> = {
  1: "Az akor, rahat geçişler. Isınmak ve çarpanı ilk kez ×8'e çıkarmak için.",
  2: "Daha çok akor ve daha sık geçiş; barre ya da 7'li akorlar da var.",
  3: "7'li ve barre akorlar, hızlı değişimler. Önce yavaş tempoda dene.",
};

function JamCard({ s }: { s: PreparedSong }) {
  const go = useGo();
  const rec = loadJamRecord(s.id);
  const chart = buildChart(s);
  return (
    <button type="button" className="song-card jam-card" data-song={s.id} onClick={() => go(`jam.${s.id}`)}>
      <div className="sg-meta">
        <span className={`sg-tag lv${s.level}`}>{LEVEL_NAME[s.level]}</span>
        <span className="sg-tag">{s.cover ? s.cover.artist : 'kulaktan.com'}</span>
        {s.lyrics?.length ? <span className="sg-tag">Sözlü</span> : null}
        <span className="sg-tag">{s.bpm} BPM</span>
      </div>
      <h3>{s.title}</h3>
      <p>
        {fmtTime(chart.length)} · {chart.lanes.length} akor · {s.bars.length} ölçü
      </p>
      <div className="sg-status jam-best">
        {rec ? (
          <>
            <Stars n={rec.stars} />
            <span>
              En iyi {Math.round(rec.best).toLocaleString('tr-TR')} · %{Math.round(rec.acc * 100)}
            </span>
          </>
        ) : (
          <span>Henüz çalmadın</span>
        )}
      </div>
    </button>
  );
}

function JamHome() {
  useProgress();
  return (
    <div className="jam-home" style={modStyle('akor')}>
      <header className="mod-head">
        <div className="mod-icon">
          <Icon name="jam" />
        </div>
        <div className="mod-titles">
          <h1>Jam</h1>
          <p>
            Şarkını seç, akorlar vuruş çizgisine aksın. Mikrofon gitarını dinler: doğru çaldıkça çarpan büyür, hata
            yaparsan seri baştan başlar.
          </p>
        </div>
      </header>
      <ol className="jam-how">
        <li>
          <b>Seç</b>
          <span>Zorluğa göre bir şarkı al, eşliği ve tempoyu ayarla.</span>
        </li>
        <li>
          <b>Çal</b>
          <span>Akor çizgiye geldiğinde gitarda çal. Mikrofon akoru tanır.</span>
        </li>
        <li>
          <b>Katla</b>
          <span>Art arda doğrular çarpanı ×2, ×4, ×8 yapar. Yanlışta ×1'e dönersin.</span>
        </li>
      </ol>
      {([1, 2, 3] as const).map((lv) => {
        const list = SONGS.filter((s) => s.level === lv);
        if (!list.length) return null;
        return (
          <section key={lv} className="jam-group">
            <GroupTitle title={LEVEL_NAME[lv] as string} sub={LEVEL_SUB[lv]} />
            <div className="song-list">
              {[...list.filter((s) => !s.cover), ...list.filter((s) => s.cover)].map((s) => (
                <JamCard key={s.id} s={s} />
              ))}
            </div>
          </section>
        );
      })}
      <p className="mb-note jam-note">
        Sevilen şarkılarda yalnızca akorlar var; sözler telifli olduğu için gösterilmez. kulaktan.com'un kendi
        şarkılarında sözler müzikle birlikte akar.
      </p>
    </div>
  );
}

function JamSongRoute({ id }: { id: string }) {
  const go = useGo();
  const song = songById(id);
  useEffect(() => {
    if (!song) go(JAM_ROUTE);
  }, [song, go]);
  return song ? <JamGame key={song.id} song={song} /> : null;
}

export function JamSection() {
  const [id] = useSubPath();
  return id ? <JamSongRoute id={id} /> : <JamHome />;
}
